import {
  Box,
  ClickAwayListener,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Popper,
  TextField,
  type TextFieldProps,
  Tooltip,
  Typography,
} from "@mui/material";
import type React from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { TestVariable } from "../types";

/**
 * Deduplicate variables by key, keeping the first occurrence (highest precedence).
 * Variables should be passed in precedence order: global > app_config > check-specific
 */
const deduplicateVariables = (variables: TestVariable[]): TestVariable[] => {
  const seen = new Set<string>();
  return variables.filter((v) => {
    if (seen.has(v.key)) return false;
    seen.add(v.key);
    return true;
  });
};

interface VariableInputProps extends Omit<TextFieldProps, "value" | "onChange"> {
  value: string;
  onChange: (value: string) => void;
  variables: TestVariable[];
}

// Regex to find {{variable}} patterns (hoisted to module scope per React best practices)
// Note: Since this is global (g flag), we need a fresh instance for each exec loop
const VARIABLE_REGEX = /\{\{([^}]+)\}\}/g;

export const VariableInput: React.FC<VariableInputProps> = ({
  value,
  onChange,
  variables,
  multiline,
  ...textFieldProps
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [cursorPosition, setCursorPosition] = useState(0);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);

  // Deduplicate variables by key, keeping highest precedence (first occurrence)
  const deduplicatedVariables = useMemo(() => deduplicateVariables(variables), [variables]);

  // Find the trigger position (where {{ starts)
  const triggerInfo = useMemo(() => {
    const textBeforeCursor = value.slice(0, cursorPosition);
    const lastOpenBrace = textBeforeCursor.lastIndexOf("{{");

    if (lastOpenBrace === -1) return null;

    const textAfterBrace = textBeforeCursor.slice(lastOpenBrace + 2);
    // Check if there's a closing }} between the {{ and cursor
    if (textAfterBrace.includes("}}")) return null;

    return {
      start: lastOpenBrace,
      searchText: textAfterBrace.toLowerCase(),
    };
  }, [value, cursorPosition]);

  // Filter variables based on search text
  const filteredVariables = useMemo(() => {
    if (!triggerInfo) return [];
    return deduplicatedVariables.filter((v) =>
      v.key.toLowerCase().includes(triggerInfo.searchText),
    );
  }, [deduplicatedVariables, triggerInfo]);

  // Show suggestions when we have a trigger and matching variables
  useEffect(() => {
    setShowSuggestions(triggerInfo !== null && filteredVariables.length > 0);
    setSelectedIndex(0);
  }, [triggerInfo, filteredVariables.length]);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      onChange(e.target.value);
      // Use ?? instead of || to handle selectionStart being 0
      setCursorPosition(e.target.selectionStart ?? 0);
      setAnchorEl(e.target);
    },
    [onChange],
  );

  const handleSelect = useCallback((e: React.SyntheticEvent) => {
    const target = e.target as HTMLInputElement;
    setCursorPosition(target.selectionStart ?? 0);
  }, []);

  const handleFocus = useCallback((e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setCursorPosition(e.target.selectionStart ?? 0);
    setAnchorEl(e.target);
  }, []);

  const insertVariable = useCallback(
    (variable: TestVariable) => {
      if (!triggerInfo) return;

      const before = value.slice(0, triggerInfo.start);
      const after = value.slice(cursorPosition);
      const insertion = `{{${variable.key}}}`;
      const newValue = before + insertion + after;

      onChange(newValue);
      setShowSuggestions(false);

      // Set cursor position after the inserted variable
      const newCursorPos = triggerInfo.start + insertion.length;
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.setSelectionRange(newCursorPos, newCursorPos);
          inputRef.current.focus();
        }
      }, 0);
    },
    [triggerInfo, value, cursorPosition, onChange],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (!showSuggestions) return;

      if (e.key === "ArrowDown") {
        e.preventDefault();
        setSelectedIndex((prev) => Math.min(prev + 1, filteredVariables.length - 1));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setSelectedIndex((prev) => Math.max(prev - 1, 0));
      } else if (e.key === "Enter" || e.key === "Tab") {
        if (filteredVariables[selectedIndex]) {
          e.preventDefault();
          insertVariable(filteredVariables[selectedIndex]);
        }
      } else if (e.key === "Escape") {
        setShowSuggestions(false);
      }
    },
    [showSuggestions, filteredVariables, selectedIndex, insertVariable],
  );

  const getDisplayValue = useCallback((variable: TestVariable) => {
    if (variable.is_sensitive) {
      return "\u2022\u2022\u2022\u2022\u2022\u2022\u2022";
    }
    return String(variable.value);
  }, []);

  // Find variable by key for hover tooltips
  const findVariable = useCallback(
    (key: string) => {
      return deduplicatedVariables.find((v) => v.key === key);
    },
    [deduplicatedVariables],
  );

  // Render highlighted text with variable tooltips
  const renderHighlightedValue = useMemo(() => {
    if (!value) return null;

    const parts: React.ReactNode[] = [];
    let lastIndex = 0;

    // Create fresh regex instance to avoid lastIndex issues with global flag
    // (Hoisting the pattern to module scope avoids recreation overhead)
    const regex = new RegExp(VARIABLE_REGEX.source, VARIABLE_REGEX.flags);
    for (let match = regex.exec(value); match !== null; match = regex.exec(value)) {
      // Add text before the match
      if (match.index > lastIndex) {
        parts.push(<span key={`text-${lastIndex}`}>{value.slice(lastIndex, match.index)}</span>);
      }

      // Add the variable with tooltip
      const variableKey = match[1];
      const variable = findVariable(variableKey);
      const tooltipContent = variable
        ? `${variable.key} (${variable.context}): ${getDisplayValue(variable)}`
        : `${variableKey}: undefined`;

      parts.push(
        <Tooltip key={`var-${match.index}`} title={tooltipContent} arrow>
          <Box
            component="span"
            sx={{
              backgroundColor: "rgba(103, 58, 183, 0.15)",
              borderRadius: "3px",
              px: 0.5,
              color: "#673ab7",
              fontWeight: 500,
              cursor: "pointer",
            }}
          >
            {match[0]}
          </Box>
        </Tooltip>,
      );

      lastIndex = match.index + match[0].length;
    }

    // Add remaining text
    if (lastIndex < value.length) {
      parts.push(<span key={`text-${lastIndex}`}>{value.slice(lastIndex)}</span>);
    }

    return parts;
  }, [value, findVariable, getDisplayValue]);

  return (
    <ClickAwayListener onClickAway={() => setShowSuggestions(false)}>
      <Box sx={{ position: "relative", width: "100%" }}>
        <TextField
          {...textFieldProps}
          multiline={multiline}
          value={value}
          onChange={handleInputChange}
          onSelect={handleSelect}
          onKeyDown={handleKeyDown}
          onFocus={handleFocus}
          inputRef={inputRef}
          fullWidth
        />

        {/* Overlay for highlighting (only for single-line, non-focused) */}
        {!multiline && value && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              pointerEvents: "none",
              display: "flex",
              alignItems: "center",
              px: textFieldProps.size === "small" ? 1.75 : 2,
              py: textFieldProps.size === "small" ? 1 : 1.5,
              fontSize: "0.875rem",
              fontFamily: "inherit",
              opacity: 0,
              "&:hover": {
                opacity: 0,
              },
            }}
          >
            {renderHighlightedValue}
          </Box>
        )}

        <Popper
          open={showSuggestions}
          anchorEl={anchorEl}
          placement="bottom-start"
          style={{ zIndex: 1500 }}
        >
          <Paper
            elevation={8}
            sx={{
              maxHeight: 200,
              overflow: "auto",
              minWidth: 250,
              mt: 0.5,
            }}
          >
            <List dense disablePadding>
              {filteredVariables.map((variable, index) => (
                <ListItemButton
                  key={variable.key}
                  selected={index === selectedIndex}
                  onClick={() => insertVariable(variable)}
                  sx={{ py: 0.5 }}
                >
                  <ListItemText
                    primary={
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <Typography
                          variant="body2"
                          fontWeight={500}
                          sx={{ fontFamily: "monospace" }}
                        >
                          {`{{${variable.key}}}`}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
                          {variable.context}
                        </Typography>
                      </Box>
                    }
                    secondary={
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          fontFamily: "monospace",
                          fontSize: "0.7rem",
                        }}
                      >
                        {getDisplayValue(variable)}
                      </Typography>
                    }
                  />
                </ListItemButton>
              ))}
            </List>
          </Paper>
        </Popper>
      </Box>
    </ClickAwayListener>
  );
};

export default VariableInput;
